import React, { useEffect, useState } from "react";
import { DndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { getOrg } from "../../api/axios"; // API za dobavljanje podataka
import { Box, Typography, List, ListItem, IconButton } from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const OrgUnits = () => {
  const [treeData, setTreeData] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState({});

  // Funkcija za transformaciju podataka u hijerarhijsku strukturu
  const transformToTree = (data) => {
    const map = {};
    const tree = [];

    data.forEach((item) => {
      const itemId = item.id || `${item.name}-${Math.random()}`;
      map[itemId] = { ...item, id: itemId, children: [] };
    });

    data.forEach((item) => {
      const itemId = item.id || `${item.name}-${Math.random()}`;
      if (item.parent_id) {
        if (map[item.parent_id]) {
          map[item.parent_id].children.push(map[itemId]);
        } else {
          console.warn(`Parent ID ${item.parent_id} not found for item`, item);
        }
      } else {
        tree.push(map[itemId]);
      }
    });

    return tree;
  };

  // Funkcija za ažuriranje stabla kada se izvrši drag and drop
  const handleDragEnd = (event) => {
    if (!event || !event.active || !event.over) {
      console.warn("Invalid drag event:", event);
      return;
    }

    const { active, over } = event;

    if (active.id === over.id) {
      console.log("Element dropped onto itself.");
      return;
    }

    setTreeData((prevTree) => {
      const newTree = JSON.parse(JSON.stringify(prevTree));
      let draggedItem = null;

      const removeItem = (nodes, id) => {
        return nodes.filter((node) => {
          if (node.id === id) {
            draggedItem = node;
            return false;
          }
          if (node.children) {
            node.children = removeItem(node.children, id);
          }
          return true;
        });
      };

      const isChildOf = (parentId, childId, nodes) => {
        for (const node of nodes) {
          if (node.id === parentId) {
            if (node.children.some((child) => child.id === childId)) {
              return true;
            }
            return node.children.some((child) =>
              isChildOf(child.id, childId, node.children)
            );
          }
          if (node.children) {
            if (isChildOf(parentId, childId, node.children)) {
              return true;
            }
          }
        }
        return false;
      };

      const addItem = (nodes, overId) => {
        if (overId === "root") {
          nodes.push(draggedItem);
          return;
        }

        nodes.forEach((node) => {
          if (node.id === overId) {
            node.children = node.children || [];
            node.children.push(draggedItem);
          } else if (node.children) {
            addItem(node.children, overId);
          }
        });
      };

      const updatedTree = removeItem(newTree, active.id);

      if (isChildOf(active.id, over.id, prevTree)) {
        console.warn("Cannot drop onto a child or grandchild of the active item.");
        return prevTree;
      }

      addItem(updatedTree, over.id);

      return updatedTree;
    });
  };

  const DraggableItem = ({ id, children, isExpanded }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
      id,
    });
  
    const style = {
      transform: transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
      cursor: isDragging ? "grabbing" : "pointer",
      transition: "transform 0.1s ease-out",
    };
  
    return (
      <Box
        ref={setNodeRef}
        sx={{
          ...style,
          padding: "5px",
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}
        {...attributes}
        {...listeners}
      >
        {isExpanded ? <FolderOpenIcon color="primary" /> : <FolderIcon color="primary" />}
        {children}
      </Box>
    );
  };
  

  const DroppableItem = ({ id, children, sx }) => {
    const { setNodeRef } = useDroppable({ id });

    return (
      <Box
        ref={setNodeRef}
        sx={{
          width: "100%",
          paddingBottom: "1px",
          ...sx,
        }}
      >
        {children}
      </Box>
    );
  };

  // Rukovanje proširivanjem/smanjivanjem čvorova
  const toggleExpand = (id) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderTree = (nodes) => {
    const isExpanded = expandedNodes[nodes.id];
  
    return (
      <DroppableItem key={nodes.id} id={nodes.id}>
        <Box display="flex" alignItems="center" gap="10px">
          {nodes.children && nodes.children.length > 0 ? (
            <IconButton onClick={() => toggleExpand(nodes.id)} size="small">
              {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          ) : (
            <Box width="33px" />
          )}
          <DraggableItem id={nodes.id} isExpanded={isExpanded && nodes.children && nodes.children.length > 0}>
            <Typography variant="body1">{nodes.name}</Typography>
          </DraggableItem>
        </Box>
        {isExpanded && nodes.children && nodes.children.length > 0 && (
          <List sx={{ paddingTop: "0px", paddingBottom: "0px" }}>
            {nodes.children.map((child) => (
              <ListItem
                key={child.id}
                sx={{
                  marginLeft: "10px",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                  borderLeft: "1px solid var(--contrast-color)",
                }}
              >
                {renderTree(child)}
              </ListItem>
            ))}
          </List>
        )}
      </DroppableItem>
    );
  };
  


  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        const response = await getOrg(accessToken);
        const transformedData = transformToTree(response.data.organizations);
        setTreeData(transformedData);
      } catch (error) {
        console.error("Error fetching organizations:", error);
      }
    };

    fetchData();
  }, []);

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <DroppableItem id="root">
        <Box sx={{ padding: "24px" }}>
          <Typography variant="h4" gutterBottom>
            Organizacione jedinice
          </Typography>
          <List style={{ listStyle: "none", padding: 0 }}>
            {treeData.map((node) => renderTree(node))}
          </List>
        </Box>
      </DroppableItem>
    </DndContext>
  );
};

export default OrgUnits;

